// InlineAddServer — a compact "Add New Server" mini-form shown under the Server
// dropdown on the Add / Edit Domain forms. When the server the user wants isn't
// in the dropdown, they add it here: name + public IP + hosting provider are
// saved as a new row in the `servers` table (POST /servers), and the created
// server is handed back so the caller can select it in the dropdown.
//
// `ownership` is preset from the domain's "separate hosting" choice:
//   "client" (separate hosting → a client-owned server) or "self" (own server).
//
// Props:
//   ownership  — "client" | "self"
//   onCreated  — (server) => void   the created server object (POST response)
//   onCancel   — () => void
//   fieldSx / labelSx — styling passed down from the host form
import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import api from "../api";

export default function InlineAddServer({
  ownership = "self",
  onCreated,
  onCancel,
  fieldSx,
  labelSx,
}) {
  const [name, setName] = useState("");
  const [publicIp, setPublicIp] = useState("");
  const [provider, setProvider] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSave() {
    const n = name.trim();
    if (!n) {
      setError("Server name is required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const created = await api.post("/servers", {
        name: n,
        ownership,
        publicIp: publicIp.trim() || null,
        hostingProvider: provider.trim() || null,
      });
      onCreated?.(created);
    } catch (err) {
      setError(err.message || "Failed to add server");
      setSaving(false);
    }
  }

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 2,
        borderRadius: "12px",
        border: "1px dashed #DBE3EF",
        background: "#F8FAFC",
      }}
    >
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A", mb: 1.5 }}>
        Add New Server{ownership === "client" ? " (Client-owned)" : ""}
      </Typography>
      <Stack spacing={1.5}>
        <Box>
          <Typography component="label" sx={labelSx}>
            Server Name
          </Typography>
          <TextField
            placeholder="e.g. web-prod-01"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSave();
              }
            }}
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.5,
          }}
        >
          <Box>
            <Typography component="label" sx={labelSx}>
              Public IP
            </Typography>
            <TextField
              placeholder="e.g. 203.0.113.1"
              value={publicIp}
              onChange={(e) => setPublicIp(e.target.value)}
              fullWidth
              size="small"
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>
              Hosting Provider
            </Typography>
            <TextField
              placeholder="e.g. AWS, DigitalOcean"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              fullWidth
              size="small"
              sx={fieldSx}
            />
          </Box>
        </Box>
        {error && (
          <Typography sx={{ fontSize: 12, color: "#DC2626" }}>{error}</Typography>
        )}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            onClick={onCancel}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              color: "#64748B",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            startIcon={
              saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              color: "#fff",
              background: "#3B82F6",
              px: 2,
              "&:hover": { background: "#1D4ED8" },
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.7)",
                background: "#BFD3F2",
              },
            }}
          >
            {saving ? "Saving…" : "Save Server"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
